import { CS_m } from 'back-end-api'

import { useMiscState } from './context'
import { audioVisuals } from './reducer'

export interface AudioVisualsProps {
  pickedUpAppointmentId: number | null
  appointmentSnoozedIds: number[]
  inProgressAppointmentId: number | null
  /**
   * Explicit request from the user to re-open the incoming-call popup for a
   * specific appointment. This overrides the snooze/processed filters so a
   * doctor can recover a call after clicking "Pick up later" or clicking the
   * phone icon in the overview list. Cleared once the popup is shown.
   */
  requestedOpenAppointmentId: number | null
}

export const useAudioVisualsState = (): AudioVisualsProps => {
  const miscState = useMiscState()

  return {
    pickedUpAppointmentId: miscState.audioVisuals?.pickedUpAppointmentId ?? null,
    appointmentSnoozedIds: miscState.audioVisuals?.appointmentSnoozedIds ?? [],
    inProgressAppointmentId: miscState.audioVisuals?.inProgressAppointmentId ?? null,
    requestedOpenAppointmentId: miscState.audioVisuals?.requestedOpenAppointmentId ?? null,
  }
}

const setAudioVisuals = (miscDispatch: any, appointmentSideBarProps: AudioVisualsProps): void => {
  miscDispatch({ type: audioVisuals, payload: appointmentSideBarProps })
}

export const setAppointmentPickedUp = (
  miscDispatch: any,
  state: AudioVisualsProps,
  appointment: CS_m.ModelsAppointment
): void => {
  setAudioVisuals(miscDispatch, {
    pickedUpAppointmentId: appointment.id,
    appointmentSnoozedIds: state.appointmentSnoozedIds.filter(id => id !== appointment.id),
    inProgressAppointmentId: null,
    requestedOpenAppointmentId: null,
  })
}

export const setAppointmentSnoozed = (
  miscDispatch: any,
  state: AudioVisualsProps,
  appointment: CS_m.ModelsAppointment
): void => {
  setAudioVisuals(miscDispatch, {
    pickedUpAppointmentId: state.pickedUpAppointmentId,
    appointmentSnoozedIds: [...state.appointmentSnoozedIds, appointment.id],
    inProgressAppointmentId: state.inProgressAppointmentId,
    requestedOpenAppointmentId: null,
  })
}

export const setHangUpAppointment = (miscDispatch: any, state: AudioVisualsProps): void => {
  setAudioVisuals(miscDispatch, {
    pickedUpAppointmentId: null,
    appointmentSnoozedIds: state.appointmentSnoozedIds,
    inProgressAppointmentId: null,
    requestedOpenAppointmentId: null,
  })
}

export const setInProgressAppointment = (
  miscDispatch: any,
  state: AudioVisualsProps,
  appointmentId: number | null
): void => {
  setAudioVisuals(miscDispatch, {
    pickedUpAppointmentId: state.pickedUpAppointmentId,
    appointmentSnoozedIds: state.appointmentSnoozedIds,
    inProgressAppointmentId: appointmentId,
    requestedOpenAppointmentId: state.requestedOpenAppointmentId,
  })
}

/**
 * Ask AVConsult to open the incoming-call popup for a specific appointment,
 * bypassing snooze/processed filters. Used when the doctor clicks the phone
 * icon in the appointments overview or wants to re-surface a snoozed call.
 */
export const requestOpenAppointmentCall = (
  miscDispatch: any,
  state: AudioVisualsProps,
  appointmentId: number
): void => {
  setAudioVisuals(miscDispatch, {
    pickedUpAppointmentId: state.pickedUpAppointmentId,
    appointmentSnoozedIds: state.appointmentSnoozedIds.filter(id => id !== appointmentId),
    inProgressAppointmentId: state.inProgressAppointmentId,
    requestedOpenAppointmentId: appointmentId,
  })
}

export const clearRequestOpenAppointmentCall = (
  miscDispatch: any,
  state: AudioVisualsProps
): void => {
  if (state.requestedOpenAppointmentId === null) return
  setAudioVisuals(miscDispatch, {
    pickedUpAppointmentId: state.pickedUpAppointmentId,
    appointmentSnoozedIds: state.appointmentSnoozedIds,
    inProgressAppointmentId: state.inProgressAppointmentId,
    requestedOpenAppointmentId: null,
  })
}
